/* eslint-disable max-len */
import React, { useEffect } from "react";
import "./style.scss";
import { QlikProvider } from "./context/Qlik";
import { FilterBarProvider } from "./context/FilterBar";
import { BookMarkProvider } from "./context/BookMarkContainer";
import { LeftBarProvider } from "./context/LeftBar";
import { ConfigProvider } from "./context/Config";
import Layout from "./pages/Layout";
import { FiltersProvider } from "./context/Filters";
import { ModalProvider } from "./context/Modal";
import { ExportIdProvider } from "./context/ExportId";
import { SelectionsProvider } from "./context/Selections";
import { setCookie } from "nookies";

const LOGIN_WEBSITE = process.env.REACT_APP_LOGIN_WEBSITE || "";

/**
 * Valida expiração do JWT pelo "exp" do payload.
 */
function isJwtExpired(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return true;
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    if (!payload || !payload.exp) return true;
    const nowInSeconds = Math.floor(Date.now() / 1000);
    return payload.exp <= nowInSeconds;
  } catch {
    return true;
  }
}

function App() {
  useEffect(() => {
    try {
      const secureFlag =
        typeof window !== "undefined" &&
        window.location &&
        window.location.protocol === "https:";

      // ---- 1) Coleta credenciais da QUERY ---------------------------------
      const query = new URLSearchParams(window.location.search);
      const token = query.get("token");
      const refreshToken = query.get("refreshtoken");
      const userHashQuery = query.get("u");

      // ---- 2) Coleta possível userHash no HASH: #<hash>/overview ----------
      let userHashHash = null;
      const rawHash =
        (typeof window !== "undefined" ? window.location.hash : "") || "";
      if (rawHash) {
        const cleaned = rawHash.replace(/^#/, "");
        const firstSeg = cleaned.split("/")[0];
        // Ajuste o regex conforme o formato real do seu hash
        if (/^[0-9a-f]{32}$/i.test(firstSeg)) {
          userHashHash = firstSeg;
        }
      }

      // ---- 3) Fluxo com tokens na QUERY -----------------------------------
      if (token && refreshToken) {
        setCookie(null, "access_token", token, {
          path: "/",
          maxAge: 60 * 60 * 24 * 7, // 7 dias
          secure: secureFlag,
          sameSite: "lax",
        });
        setCookie(null, "refresh_token", refreshToken, {
          path: "/",
          maxAge: 60 * 60 * 24 * 7, // 7 dias
          secure: secureFlag,
          sameSite: "lax",
        });

        // Tokens inválidos → login
        if (isJwtExpired(token) || isJwtExpired(refreshToken)) {
          if (LOGIN_WEBSITE) window.location.replace(LOGIN_WEBSITE);
          return;
        }

        // Limpa URL (remove ?… e qualquer hash sujo) e deixa #/overview
        const clean = `${window.location.origin}${window.location.pathname}#/overview`;
        window.history.replaceState(null, "", clean);
        return;
      }

      // ---- 4) Fluxo com userHash (QUERY OU HASH) --------------------------
      const finalUserHash = userHashQuery || userHashHash;
      if (finalUserHash) {
        setCookie(null, "userHash", finalUserHash, {
          path: "/",
          maxAge: 60 * 60 * 24 * 7, // 7 dias (ajuste se quiser mais)
          secure: secureFlag,
          sameSite: "lax",
          // Só usa domain em produção
          ...(window.location.hostname !== 'localhost' && { domain: ".mtrix.com.br" }),
        });

        // Limpa URL e deixa #/overview (HashRouter “limpo”)
        const clean = `${window.location.origin}${window.location.pathname}#/overview`;
        window.history.replaceState(null, "", clean);
        return;
      }

      // ---- 5) Sem credenciais → login -------------------------------------
      if (LOGIN_WEBSITE) {
        window.location.replace(LOGIN_WEBSITE);
      }
    } catch {
      if (LOGIN_WEBSITE) window.location.replace(LOGIN_WEBSITE);
    }
  }, []);

  // ---- Config Qlik com fix do NaN -----------------------------------------
  const rawPort = process.env.REACT_APP_PORT;
  const parsedPort = Number.parseInt(String(rawPort ?? ""), 10);

  const config = {
    host: process.env.REACT_APP_HOST,
    prefix: process.env.REACT_APP_PREFIX,
    port: Number.isNaN(parsedPort) ? undefined : parsedPort,
    isSecure: String(process.env.REACT_APP_IS_SECURE) === "true",
  };

  return (
    <QlikProvider config={config}>
      <SelectionsProvider>
        <BookMarkProvider>
          <ModalProvider>
            <FilterBarProvider>
              <LeftBarProvider>
                <ConfigProvider>
                  <FiltersProvider>
                    <ExportIdProvider>
                      <Layout />
                    </ExportIdProvider>
                  </FiltersProvider>
                </ConfigProvider>
              </LeftBarProvider>
            </FilterBarProvider>
          </ModalProvider>
        </BookMarkProvider>
      </SelectionsProvider>
    </QlikProvider>
  );
}

export default App;
